// Frontend email service that calls Netlify functions
// Email templates are now handled by the backend Netlify function

// Email sending functions that call Netlify functions
export const sendGalleryNotification = async (data: {
  clientEmail: string;
  clientName: string;
  galleryTitle: string;
  galleryToken: string;
  expiresAt?: string;
  imageCount: number;
  price: number;
  language?: string;
}) => {
  try {
    const galleryUrl = `${window.location.origin}/${data.language || 'en'}/client/${data.galleryToken}`;
    
    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'gallery-notification',
        data: {
          ...data,
          galleryUrl
        }
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    console.log('Gallery notification sent:', result);
    return { success: true, emailId: result.emailId };
  } catch (error) {
    console.error('Failed to send gallery notification:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to send email';
    
    // If running in development without Netlify functions, return a warning instead of error
    if (errorMessage.includes('<!DOCTYPE') || errorMessage.includes('Failed to fetch')) {
      console.warn('Email service not available (likely running npm start instead of netlify dev)');
      return { 
        success: false, 
        error: 'Email service not available in development mode'
      };
    }
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

export const sendPurchaseConfirmation = async (data: {
  clientEmail: string;
  clientName: string;
  galleryTitle: string;
  galleryToken: string;
  amount: number;
  imageCount: number;
  paymentMethod?: string;
  transactionId?: string;
  language?: string;
}) => {
  try {
    const galleryUrl = `${window.location.origin}/${data.language || 'en'}/client/${data.galleryToken}`;
    
    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'purchase-confirmation',
        data: {
          ...data,
          galleryUrl,
          paymentMethod: data.paymentMethod || 'PayPal'
        }
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    console.log('Purchase confirmation sent:', result);
    return { success: true, emailId: result.emailId };
  } catch (error) {
    console.error('Failed to send purchase confirmation:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to send email' 
    };
  }
};

export const sendAdminPurchaseNotification = async (data: {
  clientEmail: string;
  clientName: string;
  galleryTitle: string;
  galleryToken: string;
  amount: number;
  imageCount: number;
  paymentMethod?: string;
  transactionId?: string;
  language?: string;
}) => {
  try {
    const galleryUrl = `${window.location.origin}/${data.language || 'en'}/client/${data.galleryToken}`;
    
    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'admin-purchase-notification',
        data: {
          ...data,
          galleryUrl,
          paymentMethod: data.paymentMethod || 'PayPal'
        }
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    console.log('Admin purchase notification sent:', result);
    return { success: true, emailId: result.emailId };
  } catch (error) {
    console.error('Failed to send admin purchase notification:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to send admin notification' 
    };
  }
};

// Test email function for development
export const sendTestEmail = async (toEmail: string) => {
  try {
    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'test',
        data: {
          email: toEmail
        }
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    console.log('Test email sent:', result);
    return { success: true, emailId: result.emailId };
  } catch (error) {
    console.error('Test email failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to send test email' 
    };
  }
};